// src/ringcentral.js
// ─────────────────────────────────────────────────────────────
// RingCentral integration helper (browser-only).
//   • OAuth 2.0 Authorization Code flow with PKCE (no client secret).
//   • WebSocket subscription to telephony session events for inbound call
//     detection. The actual call is handled by the RC desktop/mobile app.
//   • Deep-link helper for click-to-call — launches the RC app via
//     rcapp:// (desktop), with rcmobile:// and tel: fallbacks.
//
// Env vars (optional — Client ID can also be set in Settings UI):
//   REACT_APP_RC_CLIENT_ID    — RingCentral app Client ID (public/PKCE app)
//   REACT_APP_RC_SERVER_URL   — https://platform.ringcentral.com  (prod)
//                             — https://platform.devtest.ringcentral.com (sandbox)
// ─────────────────────────────────────────────────────────────

const DEFAULT_SERVER = "https://platform.ringcentral.com";
// The WebSocket host comes from /restapi/oauth/wstoken (e.g.
// wss://ws-api.ringcentral.com/ws) — see subscribeInboundCalls.
const LS_PREFIX = "rc_";

// ── Storage helpers ──────────────────────────────────────────
const store = {
  get: (k) => { try { return localStorage.getItem(LS_PREFIX + k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(LS_PREFIX + k, v); } catch {} },
  del: (k) => { try { localStorage.removeItem(LS_PREFIX + k); } catch {} },
};

// ── Config access ────────────────────────────────────────────
export function getRcConfig(settings) {
  const envClientId = process.env.REACT_APP_RC_CLIENT_ID || "";
  const envServer = process.env.REACT_APP_RC_SERVER_URL || "";
  return {
    clientId: (settings && settings.rcClientId) || envClientId || "",
    server: (settings && settings.rcServerUrl) || envServer || DEFAULT_SERVER,
    redirectUri: (typeof window !== "undefined" ? window.location.origin : "") + "/rc-callback",
  };
}

export function isRcConfigured(settings) {
  const c = getRcConfig(settings);
  return !!c.clientId && !!c.server;
}

// ── PKCE helpers ─────────────────────────────────────────────
function b64url(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function makePkcePair() {
  const arr = new Uint8Array(32);
  window.crypto.getRandomValues(arr);
  const verifier = b64url(arr);
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const challenge = b64url(digest);
  return { verifier, challenge };
}

function makeState() {
  const arr = new Uint8Array(16);
  window.crypto.getRandomValues(arr);
  return b64url(arr);
}

// ── Token management ─────────────────────────────────────────
export function getStoredTokens() {
  const access = store.get("access_token");
  const refresh = store.get("refresh_token");
  const expiresAt = parseInt(store.get("expires_at") || "0", 10);
  if (!access) return null;
  return { access, refresh, expiresAt };
}

function saveTokens(data) {
  const now = Date.now();
  if (data.access_token) store.set("access_token", data.access_token);
  if (data.refresh_token) store.set("refresh_token", data.refresh_token);
  if (data.expires_in) store.set("expires_at", String(now + data.expires_in * 1000));
  // The permissions this token was granted (space-separated) — the call-back
  // list reads it to know "Read Call Log" is missing without asking.
  if (data.scope) store.set("scope", String(data.scope));
}

export function clearTokens() {
  store.del("access_token");
  store.del("refresh_token");
  store.del("expires_at");
  store.del("pkce_verifier");
  store.del("oauth_state");
  store.del("scope");
}

export function isAuthed() {
  const t = getStoredTokens();
  return !!(t && t.access);
}

// The token's permissions, or null when not known (a token saved before
// 10/10/26, or a token response without `scope`).
export function grantedScopes() {
  const s = store.get("scope");
  return s ? s.split(/[\s,]+/).filter(Boolean) : null;
}
// True only when the token's permissions are KNOWN and lack ReadCallLog.
export function callLogScopeMissing() {
  const s = grantedScopes();
  return !!s && s.indexOf("ReadCallLog") < 0;
}

// ── OAuth flow ───────────────────────────────────────────────
export async function startOAuth(settings) {
  const { clientId, server, redirectUri } = getRcConfig(settings);
  if (!clientId) throw new Error("RingCentral Client ID not set");
  const { verifier, challenge } = await makePkcePair();
  const state = makeState();
  store.set("pkce_verifier", verifier);
  store.set("oauth_state", state);
  const url = new URL(server + "/restapi/oauth/authorize");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("state", state);
  window.location.assign(url.toString());
}

// Exchange auth code for tokens. Returns tokens on success.
export async function exchangeCode(code, state, settings) {
  const { clientId, server, redirectUri } = getRcConfig(settings);
  const expectedState = store.get("oauth_state");
  const verifier = store.get("pkce_verifier");
  if (expectedState && state && expectedState !== state) throw new Error("OAuth state mismatch");
  if (!verifier) throw new Error("Missing PKCE verifier — restart auth");
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    client_id: clientId,
    code_verifier: verifier,
  });
  const res = await fetch(server + "/restapi/oauth/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded", "Accept": "application/json" },
    body,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error("Token exchange failed: " + res.status + " " + txt);
  }
  const data = await res.json();
  saveTokens(data);
  store.del("pkce_verifier");
  store.del("oauth_state");
  return data;
}

export async function refreshAccessToken(settings) {
  const { clientId, server } = getRcConfig(settings);
  const t = getStoredTokens();
  if (!t || !t.refresh) throw new Error("No refresh token");
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: t.refresh,
    client_id: clientId,
  });
  const res = await fetch(server + "/restapi/oauth/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded", "Accept": "application/json" },
    body,
  });
  if (!res.ok) {
    clearTokens();
    throw new Error("Refresh failed: " + res.status);
  }
  const data = await res.json();
  saveTokens(data);
  return data;
}

async function getValidAccessToken(settings) {
  const t = getStoredTokens();
  if (!t) return null;
  const now = Date.now();
  if (!t.expiresAt || t.expiresAt - now < 5 * 60 * 1000) {
    try { await refreshAccessToken(settings); } catch { return null; }
  }
  return (getStoredTokens() || {}).access || null;
}

// Handle ?code=... on page load. Returns true if a callback was handled.
export async function handleOAuthCallbackIfPresent(settings) {
  if (typeof window === "undefined") return false;
  const qs = new URLSearchParams(window.location.search);
  const code = qs.get("code");
  const state = qs.get("state");
  if (!code) return false;
  try {
    await exchangeCode(code, state, settings);
  } catch (e) {
    console.warn("RC OAuth callback error:", e.message || e);
  }
  // Clean the URL so a refresh doesn't re-trigger
  const clean = window.location.pathname.replace(/\/rc-callback$/, "/");
  window.history.replaceState({}, document.title, clean + window.location.hash);
  return true;
}

// ── WebSocket subscription to telephony session events ──────
// RingCentral's WebSocket protocol (developers.ringcentral.com/guide/
// notifications/websockets): the REST access token is NOT accepted by the
// socket. Each connect first POSTs /restapi/oauth/wstoken (with a fresh,
// refreshed-if-needed access token) for a short-lived {uri, ws_access_token},
// opens uri?access_token=ws_access_token, and sends the subscription as a
// JSON ARRAY [header, body]. Notifications arrive as [header, body] with
// header.type "ServerNotification".
//
// The old code opened ws.ringcentral.com with the REST token and sent a single
// object, so no subscription was ever created; it also reused the token it
// captured once, so every reconnect after the ~1h expiry failed for good.
//
// onInbound({ from, to, sessionId, raw }) is called when a new inbound call
// is detected (party direction=Inbound, status=Setup|Proceeding). onStatus
// (optional) gets "connected" | "reconnecting" | "error". onEnded (optional,
// 10/10/26) gets { sessionId } once per party that is Disconnected — any
// direction; a session with several parties may tell it more than once, the
// last being the real hang-up — so the call-back list can re-read the call
// log ~45 s after the call ends.
// Resolves with dispose() only once the FIRST subscription is confirmed, so a
// caller that shows "connected" on resolve is telling the truth; rejects (and
// stops retrying) if that first attempt fails.
const WS_EVENT_FILTERS = ["/restapi/v1.0/account/~/extension/~/telephony/sessions"];
const WS_FIRST_CONNECT_MS = 20000;
const WS_HEARTBEAT_MS = 30000;

async function fetchWsToken(settings) {
  const token = await getValidAccessToken(settings);
  if (!token) throw new Error("Not authenticated");
  const { server } = getRcConfig(settings);
  const res = await fetch(server + "/restapi/oauth/wstoken", {
    method: "POST",
    headers: { Authorization: "Bearer " + token, Accept: "application/json" },
  });
  if (!res.ok) throw new Error("wstoken " + res.status);
  const data = await res.json();
  if (!data || !data.uri || !data.ws_access_token) throw new Error("wstoken: unexpected response");
  return data;
}

export async function subscribeInboundCalls(settings, onInbound, onStatus, onEnded) {
  if (!(await getValidAccessToken(settings))) throw new Error("Not authenticated");
  let ws = null;
  let alive = true;
  let reconnectTimer = null;
  let heartbeatTimer = null;
  let delay = 5000;
  let settled = false;
  let resolveFirst, rejectFirst;
  const first = new Promise((res, rej) => { resolveFirst = res; rejectFirst = rej; });
  const seen = new Set(); // session IDs we've already notified on
  const ended = new Set(); // "sessionId|partyId" already reported as ended (onEnded)
  const status = (s) => { try { onStatus && onStatus(s); } catch {} };
  const msgId = () => "lf-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);

  function dispose() {
    alive = false;
    clearTimeout(reconnectTimer);
    clearInterval(heartbeatTimer);
    try { ws && ws.close(); } catch {}
    ws = null;
  }
  function fail(e) {
    if (settled) return false;
    settled = true;
    clearTimeout(firstTimer);
    dispose();
    rejectFirst(e instanceof Error ? e : new Error(String(e)));
    return true;
  }
  const firstTimer = setTimeout(() => fail(new Error("RingCentral subscription timed out")), WS_FIRST_CONNECT_MS);

  function handleNotification(body) {
    if (!body) return;
    const sessionId = body.telephonySessionId || body.sessionId;
    const parties = body.parties || [];
    // A call ended (answered, missed, outbound — any): tell the call-back
    // list, once per PARTY that disconnects. A session can have several
    // parties, and the first to drop isn't always the hang-up (a forwarded /
    // simultaneous-ring leg drops when he answers on another device), so a
    // later party's Disconnected tells it again; noteCallEnded re-arms its
    // one timer, so the read follows the LAST hang-up, and its 60 s floor
    // still holds (review CB-5, 10/10). A repeated Disconnected for the same
    // party never tells it twice. The list decides whether and when to read.
    if (onEnded && sessionId) {
      let fresh = false;
      parties.forEach(function (p) {
        if (!p || !p.status || p.status.code !== "Disconnected") return;
        const key = sessionId + "|" + (p.id || "");
        if (ended.has(key)) return;
        if (ended.size > 500) ended.clear();
        ended.add(key);
        fresh = true;
      });
      if (fresh) { try { onEnded({ sessionId: sessionId }); } catch (e) { console.warn("onEnded handler error", e); } }
    }
    // Find an inbound party that's newly setting up / ringing
    for (const p of parties) {
      if (p.direction === "Inbound" && p.status && (p.status.code === "Setup" || p.status.code === "Proceeding")) {
        const key = sessionId + "|" + (p.id || "");
        if (seen.has(key)) break;
        seen.add(key);
        const fromNum = (p.from && (p.from.phoneNumber || p.from.extensionNumber)) || "";
        const fromName = (p.from && p.from.name) || "";
        const toNum = (p.to && (p.to.phoneNumber || p.to.extensionNumber)) || "";
        try { onInbound && onInbound({ from: fromNum, fromName, to: toNum, sessionId, raw: body }); } catch (e) { console.warn("onInbound handler error", e); }
        break;
      }
    }
  }

  async function connect() {
    if (!alive) return;
    let wsTok;
    try {
      wsTok = await fetchWsToken(settings); // fresh token on EVERY (re)connect
    } catch (e) {
      console.warn("RC wstoken failed:", e.message || e);
      if (!fail(e)) scheduleReconnect();
      return;
    }
    if (!alive) return;
    const subId = msgId();
    let sock;
    try {
      sock = new WebSocket(wsTok.uri + (wsTok.uri.indexOf("?") >= 0 ? "&" : "?") + "access_token=" + encodeURIComponent(wsTok.ws_access_token));
    } catch (e) {
      console.warn("RC WS open failed:", e);
      if (!fail(e)) scheduleReconnect();
      return;
    }
    ws = sock;
    sock.onopen = () => {
      const req = [
        { type: "ClientRequest", messageId: subId, method: "POST", path: "/restapi/v1.0/subscription/" },
        { eventFilters: WS_EVENT_FILTERS, deliveryMode: { transportType: "WebSocket" } },
      ];
      try { sock.send(JSON.stringify(req)); } catch {}
      clearInterval(heartbeatTimer);
      heartbeatTimer = setInterval(() => {
        try { if (sock.readyState === 1) sock.send(JSON.stringify([{ type: "Heartbeat", messageId: msgId() }])); } catch {}
      }, WS_HEARTBEAT_MS);
    };
    sock.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      const head = Array.isArray(msg) ? msg[0] : msg;
      const payload = Array.isArray(msg) ? msg[1] : null;
      if (!head) return;
      if (head.type === "ClientResponse" && head.messageId === subId) {
        if (head.status >= 200 && head.status < 300) {
          delay = 5000;
          status("connected");
          if (!settled) { settled = true; clearTimeout(firstTimer); resolveFirst(dispose); }
        } else {
          const e = new Error("RingCentral subscription failed (" + head.status + ")");
          console.warn(e.message, payload);
          if (!fail(e)) { try { sock.close(); } catch {} }
        }
        return;
      }
      if (head.type === "Error") { console.warn("RC WS error:", payload || head); return; }
      if (head.type !== "ServerNotification") return; // ConnectionDetails, Heartbeat…
      handleNotification(payload && payload.body);
    };
    sock.onclose = () => {
      clearInterval(heartbeatTimer);
      if (ws === sock) ws = null;
      if (!alive) return;
      if (!settled) { fail(new Error("RingCentral socket closed before subscribing")); return; }
      status("reconnecting");
      scheduleReconnect();
    };
    sock.onerror = () => { try { sock.close(); } catch {} };
  }
  function scheduleReconnect() {
    if (!alive) return;
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(connect, delay);
    delay = Math.min(delay * 2, 60000);
  }
  connect();
  return first;
}

// ── Deep links (outbound click-to-call) ─────────────────────
// Normalize to E.164-ish: keep leading +, strip everything else non-digit.
export function normalizePhone(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  const plus = s.startsWith("+");
  s = s.replace(/[^0-9]/g, "");
  if (!s) return "";
  if (!plus && s.length === 10) s = "1" + s; // assume US
  return "+" + s;
}

// Primary deep link for the RingCentral Desktop app.
export function rcAppCallLink(phone) {
  const p = normalizePhone(phone);
  if (!p) return "";
  return "rcapp://r/call?number=" + encodeURIComponent(p);
}

// Mobile app scheme.
export function rcMobileCallLink(phone) {
  const p = normalizePhone(phone);
  if (!p) return "";
  return "rcmobile://call?number=" + encodeURIComponent(p);
}

export function telLink(phone) {
  const p = normalizePhone(phone);
  if (!p) return "";
  return "tel:" + p;
}

// How click-to-call launches, per device: "auto" (default) tries the
// RingCentral desktop app and falls back to tel: only if nothing took the
// call; "rcapp" never falls back; "tel" skips the RingCentral link.
const CALL_MODE_KEY = "call_launch_mode";
export function getCallLaunchMode() { const m = store.get(CALL_MODE_KEY); return m === "rcapp" || m === "tel" ? m : "auto"; }
export function setCallLaunchMode(mode) { if (mode === "rcapp" || mode === "tel") store.set(CALL_MODE_KEY, mode); else store.del(CALL_MODE_KEY); }
const RCAPP_FALLBACK_MS = 1200;

// Click-handler that tries rcapp:// first then falls back to tel:
// Call from an onClick handler. e is the synthetic event.
//
// The rcapp:// link is opened in a hidden iframe so a browser without the RC
// app never navigates away from the CRM. That needs `frame-src rcapp:` in the
// CSP (vercel.json) — without it the frame is refused and only tel: fires.
// tel: is now a FALLBACK: it runs only when the RC launch didn't take focus
// (the "Open RingCentral?" prompt or the app itself blurs the page) or was
// refused by the CSP — a working RC launch is no longer followed by a second
// tel: launch.
export function launchCall(phone, e) {
  if (e && e.preventDefault) e.preventDefault();
  const p = normalizePhone(phone);
  if (!p) return;
  const mode = getCallLaunchMode();
  const tel = () => { try { window.location.href = "tel:" + p; } catch {} };
  if (mode === "tel") { tel(); return; }
  let handled = false, blocked = false, iframe = null, timer = null;
  const done = () => {
    window.removeEventListener("blur", onBlur);
    document.removeEventListener("visibilitychange", onHide);
    document.removeEventListener("securitypolicyviolation", onCsp);
    setTimeout(() => { try { iframe && iframe.parentNode && iframe.parentNode.removeChild(iframe); } catch {} }, 1500);
  };
  const onBlur = () => { handled = true; };
  const onHide = () => { if (document.visibilityState === "hidden") handled = true; };
  const onCsp = (ev) => {
    // Reports strip a non-http URL to its scheme (or to "").
    const dir = String((ev && (ev.effectiveDirective || ev.violatedDirective)) || "");
    const uri = String((ev && ev.blockedURI) || "");
    if (blocked || !/frame-src|child-src|default-src/.test(dir) || (uri && !/^rcapp/i.test(uri))) return;
    blocked = true;
    clearTimeout(timer);
    done();
    if (mode !== "rcapp") tel();
  };
  window.addEventListener("blur", onBlur);
  document.addEventListener("visibilitychange", onHide);
  document.addEventListener("securitypolicyviolation", onCsp);
  try {
    iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = "rcapp://r/call?number=" + encodeURIComponent(p);
    document.body.appendChild(iframe);
  } catch {}
  timer = setTimeout(() => {
    if (blocked) return;
    done();
    if (!handled && mode !== "rcapp") tel();
  }, RCAPP_FALLBACK_MS);
}

// ── Phone matching — last-10-digits compare ─────────────────
export function last10(raw) {
  const s = String(raw || "").replace(/[^0-9]/g, "");
  return s.slice(-10);
}

// ── Call log (the call-back list, 10/10/26) ─────────────────
// GET /restapi/v1.0/account/~/extension/~/call-log — THIS extension's calls
// from every device on it (desk phone, RC app, mobile), not only what rang
// while a CRM tab was open. Needs the app's "Read Call Log" permission: a
// token without it gets 403 (→ "no_permission"; when the token's own scope
// already says so, nothing is asked). Simple view, up to 1,000 a page;
// navigation.nextPage is followed — on the same server only, never sending
// the token anywhere else — up to CALL_LOG_MAX_PAGES. RingCentral's "Heavy"
// group (10 a minute per user): the CALLER reads only when the list opens, on
// Refresh and after a hang-up, at most once a minute, and waits out
// Retry-After on 429 (src/views/Callbacks.jsx). Never call this on a timer.
// opts.dateFrom: ISO with Z (without it RingCentral returns the last 24 h).
// → {ok:true, records, pages, more} | {ok:false, error: "not_authenticated" |
//   "no_permission" | "rate_limited" (+ retryAfterMs) | "http_<status>" |
//   "network" | "bad_response"}
const CALL_LOG_MAX_PAGES = 3;
export async function fetchCallLog(settings, opts) {
  const o = opts || {};
  if (callLogScopeMissing()) return { ok: false, error: "no_permission" };
  const token = await getValidAccessToken(settings);
  if (!token) return { ok: false, error: "not_authenticated" };
  const { server } = getRcConfig(settings);
  const qs = new URLSearchParams({ type: "Voice", view: "Simple", perPage: "1000" });
  if (o.dateFrom) qs.set("dateFrom", o.dateFrom);
  let url = server + "/restapi/v1.0/account/~/extension/~/call-log?" + qs.toString();
  const records = [];
  let pages = 0;
  while (url && pages < CALL_LOG_MAX_PAGES) {
    let res;
    try {
      res = await fetch(url, { headers: { Authorization: "Bearer " + token, Accept: "application/json" } });
    } catch (e) { return { ok: false, error: "network" }; }
    pages++;
    if (res.status === 401) return { ok: false, error: "not_authenticated" };
    if (res.status === 403) return { ok: false, error: "no_permission" };
    if (res.status === 429) {
      const h = res.headers && typeof res.headers.get === "function" ? res.headers.get("Retry-After") : null;
      return { ok: false, error: "rate_limited", retryAfterMs: parseRetryAfter(h) };
    }
    if (!res.ok) return { ok: false, error: "http_" + res.status };
    let data;
    try { data = await res.json(); } catch (e) { return { ok: false, error: "bad_response" }; }
    ((data && data.records) || []).forEach(function (r) { records.push(r); });
    const next = data && data.navigation && data.navigation.nextPage && data.navigation.nextPage.uri;
    url = next && String(next).indexOf(server + "/") === 0 ? String(next) : null;
  }
  return { ok: true, records: records, pages: pages, more: !!url };
}
// Retry-After: seconds, or an HTTP date → ms to wait (60 s when missing /
// unreadable — the call log's own penalty window).
export function parseRetryAfter(h, now) {
  const s = String(h == null ? "" : h).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(parseFloat(s) * 1000);
  const t = Date.parse(s);
  if (s && isFinite(t)) return Math.max(0, t - (now != null ? now : Date.now()));
  return 60000;
}

// ── SMS: send + auto-discover sender number ─────────────────
// Returns the authenticated extension's first SMS-capable phone number in E.164
// format, or null if none is available. Cached in localStorage.
export async function discoverSmsFromNumber(settings) {
  const cached = store.get("sms_from");
  if (cached) return cached;
  const token = await getValidAccessToken(settings);
  if (!token) return null;
  const { server } = getRcConfig(settings);
  try {
    const res = await fetch(server + "/restapi/v1.0/account/~/extension/~/phone-number?perPage=100", {
      headers: { Authorization: "Bearer " + token, Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const records = data.records || [];
    const smsNum = records.find((r) =>
      (r.features || []).includes("SmsSender")
      && (r.status || "").toLowerCase() === "normal"
      && r.phoneNumber
    );
    if (smsNum) {
      store.set("sms_from", smsNum.phoneNumber);
      return smsNum.phoneNumber;
    }
    return null;
  } catch (e) {
    console.warn("RC sms-from discovery failed:", e);
    return null;
  }
}

export function setSmsFromOverride(num) {
  if (num) store.set("sms_from", num);
  else store.del("sms_from");
}

// Send an SMS through RingCentral. The user's RC app MUST have the "SMS and
// MMS" permission, and their extension needs an SMS-capable phone number.
// Returns { ok: true, id } on success, or { ok: false, error } on failure.
export async function sendSMS(settings, to, text, fromOverride) {
  const token = await getValidAccessToken(settings);
  if (!token) return { ok: false, error: "not_authenticated" };
  const { server } = getRcConfig(settings);
  const toNorm = normalizePhone(to);
  if (!toNorm) return { ok: false, error: "invalid_to" };
  let from = fromOverride || (await discoverSmsFromNumber(settings));
  if (!from) return { ok: false, error: "no_sms_from_number" };
  try {
    const res = await fetch(server + "/restapi/v1.0/account/~/extension/~/sms", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        from: { phoneNumber: from },
        to: [{ phoneNumber: toNorm }],
        text: String(text).slice(0, 1000),
      }),
    });
    if (!res.ok) {
      const body = await res.text();
      return { ok: false, error: "http_" + res.status, detail: body };
    }
    const data = await res.json();
    return { ok: true, id: data.id };
  } catch (e) {
    return { ok: false, error: "network", detail: String((e && e.message) || e) };
  }
}
